import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Avatar, ChevronRightIcon, IconButton, ListItem, Skeleton } from '@treams/treams-ui';

import useTalentReviewPath from '../hooks/useTalentReviewPath';
import { formatTalentReviewDeadline, talentReviewDaysLeft } from '../talentReviewDate';
import { TalentReviewIndexMoment } from '../talentReviewIndexMoments';

const MAX_AVATARS = 8;

export type TalentReviewGroupLatestActivity = NonNullable<
	graphql.TalentReviewLatestActivitiesQuery['talentReviewLatestActivities'][number]
>;

export default Skeleton(
	function TalentReviewGroupCard(props: {
		group: graphql.TalentReviewGroupsQuery['groups']['edges'][number]['node'];
		/** Latest placement write on this group, if any. */
		activity?: TalentReviewGroupLatestActivity;
		/** The viewer's own talent review cycle moment for this group, if any. */
		cycleMoment?: TalentReviewIndexMoment;
	}) {
		const { t } = useTranslation();
		const { localize } = useLocalize();
		const history = useHistory();
		const path = useTalentReviewPath();

		const activity = props.activity;
		const cycleMoment = props.cycleMoment;
		const cycleTitle = cycleMoment ? (localize(cycleMoment.momentTemplate.title).text ?? '') : '';
		const pending = cycleMoment?.status === graphql.MomentStatus.Open;
		const daysLeft = cycleMoment?.endDate ? talentReviewDaysLeft(cycleMoment.endDate) : undefined;
		const overdue = daysLeft !== undefined && daysLeft < 0;
		const members = props.group.members.edges.map(({ node }) => node);

		const activityText =
			activity &&
			t('talentReviews.v2.index.lastActivity', {
				time: moment(activity.updatedAt).fromNow(),
				name: `${activity.updatedBy.firstName} ${activity.updatedBy.lastName}`,
			});
		const submittedText =
			cycleMoment && !pending
				? t('talentReviews.v2.index.submitted', { title: cycleTitle })
				: undefined;
		const notSubmittedText =
			cycleMoment && pending
				? t(
						daysLeft === undefined
							? 'talentReviews.v2.index.notSubmitted'
							: overdue
								? 'talentReviews.v2.index.notSubmittedOverdue'
								: 'talentReviews.v2.index.notSubmittedDeadline',
						{
							title: cycleTitle,
							date: cycleMoment.endDate
								? formatTalentReviewDeadline(cycleMoment.endDate)
								: undefined,
							count: daysLeft === undefined ? undefined : Math.abs(daysLeft),
						},
					)
				: undefined;

		// ListItem renders `meta` and `metaAccent` inline; the separator is part of the text.
		const metaParts: string[] = [];
		if (activityText) metaParts.push(activityText);
		if (submittedText) metaParts.push(submittedText);
		if (notSubmittedText && metaParts.length > 0) metaParts.push('');
		const meta = metaParts.length > 0 ? metaParts.join(' • ').trimEnd() : undefined;

		return (
			<ListItem
				title={props.group.name}
				body={
					members.length > 0 && (
						<Avatar.Stack
							users={members}
							shownAvatarAmount={MAX_AVATARS}
							totalCount={props.group.members.totalCount}
							showNameTooltip
						/>
					)
				}
				meta={meta}
				metaAccent={notSubmittedText}
				metaAccentTone="warning"
				trailing={<IconButton type="secondary" icon={<ChevronRightIcon />} />}
				onClick={() => history.push(path({ groupID: props.group.id }))}
			/>
		);
	},
	function TalentReviewGroupCardSkeleton() {
		return <ListItem.Skeleton body={<Avatar.Stack.Skeleton />} />;
	},
);
